import { FormattedMessage, useIntl } from "react-intl";
import { LinkExternal01Icon } from "@/icons";
import { Link } from "@/kit/ui/link";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/kit/ui/select";
import EntityProfilePic from "@/components/EntityProfilePic";
import { EntityProfilePicFragment$key } from "./__generated__/EntityProfilePicFragment.graphql";

export interface OwnerOption {
  id: string;
  username: string;
  entity: EntityProfilePicFragment$key;
}

interface EntityOwnerSelectProps {
  options: OwnerOption[];
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
}

/// The owner picker shared by the workspace/dataset create + clone dialogs: an
/// avatar + name for each option (the viewer, their organizations, and any
/// caller-supplied entity) plus a shortcut to create a new organization.
export default function EntityOwnerSelect({
  options,
  value,
  onChange,
  onBlur,
  disabled,
}: EntityOwnerSelectProps) {
  const intl = useIntl();
  return (
    <Select
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      disabled={disabled}
    >
      <SelectTrigger>
        <SelectValue
          placeholder={intl.formatMessage({ defaultMessage: "Owner" })}
        />
      </SelectTrigger>

      <SelectContent>
        {options.map((option) => (
          <SelectItem value={option.id} key={option.id}>
            <div className="flex flex-row items-center gap-3">
              <EntityProfilePic entity={option.entity} size="6" />
              {option.username}
            </div>
          </SelectItem>
        ))}

        <SelectSeparator />

        <div className="w-full flex justify-center gap-2 p-2">
          <Link
            to="/organization/new"
            variant="secondary"
            size="sm"
            className="w-full text-grey-700"
            target="_blank"
          >
            <FormattedMessage defaultMessage="Create Org." />
            <LinkExternal01Icon className="w-5 h-5" />
          </Link>
        </div>
      </SelectContent>
    </Select>
  );
}
